import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { apiRequest, formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { Loader2 } from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [policiesData, claimsData] = await Promise.all([
          apiRequest('/policies'),
          apiRequest('/claims')
        ]);
        setPolicies(policiesData);
        setClaims(claimsData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalCoverage = policies.reduce((sum, p) => sum + (p.coverage_amount || 0), 0);
  const totalPremium = policies.reduce((sum, p) => sum + (p.premium || 0), 0);
  const pendingClaims = claims.filter(c => c.status === 'pending').length;
  const activePolicies = policies.filter(p => p.is_active).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>MY DASHBOARD</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; Dashboard
          </div>
        </div>
      </div>

      <section className="content-section" data-testid="dashboard-content">
        <div className="container-narrow" style={{maxWidth: '900px'}}>
          
          <h2>Welcome back, {user?.first_name}</h2>
          <p>Here's an overview of your insurance portfolio with Archon Insurance.</p>

          <hr className="content-divider" />

          {/* Stats */}
          <h2>Portfolio Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-gray-200 p-4 text-center" data-testid="stat-active-policies">
              <p className="text-sm text-gray-500">Active Policies</p>
              <p className="text-3xl font-bold text-[#1a365d]">{activePolicies}</p>
            </div>
            <div className="border border-gray-200 p-4 text-center" data-testid="stat-total-coverage">
              <p className="text-sm text-gray-500">Total Coverage</p>
              <p className="text-2xl font-bold text-[#1a365d]">{formatCurrency(totalCoverage)}</p>
            </div>
            <div className="border border-gray-200 p-4 text-center" data-testid="stat-annual-premium">
              <p className="text-sm text-gray-500">Annual Premium</p>
              <p className="text-2xl font-bold text-[#1a365d]">{formatCurrency(totalPremium)}</p>
            </div>
            <div className="border border-gray-200 p-4 text-center" data-testid="stat-pending-claims">
              <p className="text-sm text-gray-500">Pending Claims</p>
              <p className="text-3xl font-bold text-[var(--gold)]">{pendingClaims}</p>
            </div>
          </div>

          <hr className="content-divider" />

          {/* Quick Actions */}
          <h2>Quick Actions</h2>
          <div className="flex flex-wrap gap-3 mb-8">
            <Link to="/quote" className="btn-navy" data-testid="quick-quote">Get New Quote</Link>
            <Link to="/claims" className="btn-outline-navy" data-testid="quick-claim">File a Claim</Link>
            <Link to="/chat" className="btn-outline-navy" data-testid="quick-support">Chat Support</Link>
          </div>

          <hr className="content-divider" />

          {/* Policies */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="mb-0">Your Policies</h2>
            <Link to="/policies" className="text-[#1a365d] font-semibold text-sm uppercase tracking-wide">View All →</Link>
          </div>
          {policies.length === 0 ? (
            <p className="text-gray-500 italic">No policies yet. <Link to="/quote">Get a quote</Link> to start.</p>
          ) : (
            <div className="space-y-3">
              {policies.slice(0, 3).map((policy) => (
                <div key={policy.id} className="border border-gray-200 p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-[#1a365d] capitalize">{policy.policy_type} Insurance</p>
                    <p className="text-sm text-gray-500">{policy.policy_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(policy.coverage_amount)}</p>
                    <span className={`badge ${policy.is_active ? 'badge-success' : 'badge-neutral'}`}>
                      {policy.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <hr className="content-divider" />

          {/* Claims */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="mb-0">Recent Claims</h2>
            <Link to="/claims" className="text-[#1a365d] font-semibold text-sm uppercase tracking-wide">View All →</Link>
          </div>
          {claims.length === 0 ? (
            <p className="text-gray-500 italic">No claims filed. We hope you never need to!</p>
          ) : (
            <div className="space-y-3">
              {claims.slice(0, 3).map((claim) => (
                <div key={claim.id} className="border border-gray-200 p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-[#1a365d]">{claim.claim_number}</p>
                    <p className="text-sm text-gray-600">{claim.description}</p>
                  </div>
                  <div className="text-right">
                    <span className={`badge ${getStatusColor(claim.status)}`}>
                      {claim.status.replace('_', ' ')}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(claim.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <hr className="content-divider" />

          <div className="text-center">
            <p className="text-gray-600 mb-4">Need assistance? Contact us at <a href="mailto:monte@archoninsurance.com">monte@archoninsurance.com</a></p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DashboardPage;
